import { inject, Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import {
  Installation,
  InstallationCommand,
  InstallationCreateConnectionArgs,
  InstallationRedeemedCredentials,
  InstallationUpdateConnectionArgs
} from "src/app/backend/models/installation";
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse
} from "@angular/common/http";
import { environment } from "src/environments/environment";
import { UserNotify } from "src/app/support/notify-decorator";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { PaginatedResult } from "src/app/support/paginated-result";
import { catchError, map } from "rxjs/operators";
import { sntTranslate } from "src/app/support/translate";
import {
  HTTP_CONTEXT_TOKEN_NO_CACHE,
  HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID
} from "src/app/core/http-context-tokens";
import { Connection } from "src/app/backend/models/connection";
import { PageRequest } from "src/app/support/page-request";
import { omitUndefined } from "src/app/support/object";

@Injectable()
export class InstallationBackend {
  private readonly http = inject(HttpClient);

  createConnection(
    args: InstallationCreateConnectionArgs
  ): Observable<Connection> {
    return this.http.post<any>(
      `${environment.snoots.api.integrator}/connections`,
      args
    );
  }

  updateConnectionCredentials(
    args: InstallationUpdateConnectionArgs
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.integrator}/connections/${args.connectionId}`,
      omitUndefined({
        credentials: args.credentials,
        version: args.version,
        type: args.type
      })
    );
  }

  loadConnectionById(id: string): Observable<Connection> {
    return this.http.get<Connection>(
      `${environment.snoots.api.integrator}/connections/${id}`,
      {
        context: new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
      }
    );
  }

  @UserNotify({
    successMessage: marker("Utente scollegato"),
    errorMessage: marker("Impossibile scollegare l|utente")
  })
  deleteConnection({
    connectionId
  }: {
    connectionId: string;
  }): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.integrator}/connections/${connectionId}`
    );
  }

  @UserNotify({
    successMessage: marker("Condivisione dell|utente attivata"),
    errorMessage: marker("Impossibile attivare la condivisione dell|utente")
  })
  shareConnection({
    connectionId
  }: {
    connectionId: string;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.integrator}/connections/${connectionId}/share`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Condivisione dell|utente disattivata"),
    errorMessage: marker("Impossibile disattivare la condivisione dell|utente")
  })
  unShareConnection({
    connectionId
  }: {
    connectionId: string;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.integrator}/connections/${connectionId}/unshare`,
      {}
    );
  }

  loadConnectionMeByInstallation({
    installationId
  }: {
    installationId: string;
  }): Observable<Connection | undefined> {
    return this.http
      .get<Connection>(
        `${environment.snoots.api.integrator}/installations/${installationId}/connections/me`,
        {
          params: {}
        }
      )
      .pipe(
        catchError((error) => {
          switch ((<HttpErrorResponse>error).status) {
            case 404: {
              return of(undefined);
            }
            default: {
              return throwError(() => error);
            }
          }
        })
      );
  }

  loadConnectionByMemberId({
    installation,
    memberId
  }: {
    installation: string;
    memberId: string;
  }): Observable<Connection | undefined> {
    return this.listConnectionsByInstallation({
      installation,
      request: {
        page: 1,
        pageSize: 1000
      }
    }).pipe(
      map(({ docs }) => {
        return docs.find((connection) => connection.createdBy.id === memberId);
      })
    );
  }

  listConnectionsByInstallation({
    installation,
    request
  }: {
    installation: string;
    request?: PageRequest;
  }): Observable<PaginatedResult<Connection>> {
    let params: Record<string, any> = {
      installationId: installation,
      p: request?.page,
      s: request?.pageSize
    };

    if (request?.filterValues?.shareable != undefined) {
      params.shareable = request.filterValues.shareable;
    }

    return this.http.get<PaginatedResult<Connection>>(
      `${environment.snoots.api.integrator}/connections`,
      {
        params
      }
    );
  }

  listSharedConnections({
    installation
  }: {
    installation: string;
  }): Observable<Connection[]> {
    return this.listConnectionsByInstallation({ installation }).pipe(
      map((response) =>
        response.docs.filter((connection: Connection) => connection.shareable)
      )
    );
  }

  listAll(): Observable<Installation[]> {
    return this.http
      .get<PaginatedResult<Installation>>(
        `${environment.snoots.api.integrator}/installations`
      )
      .pipe(map((response) => response.docs));
  }

  listCommands({
    installationId,
    page,
    size
  }: {
    installationId: string;
    page: number;
    size: number;
  }): Observable<PaginatedResult<InstallationCommand>> {
    return this.http.get<PaginatedResult<InstallationCommand>>(
      `${environment.snoots.api.integrator}/logs`,
      {
        params: {
          installationId,
          p: page,
          s: size
        }
      }
    );
  }

  loadById(id: string): Observable<Installation> {
    return this.http.get<Installation>(
      `${environment.snoots.api.integrator}/installations/${id}`
    );
  }

  loadByIntegration({
    integrationId
  }: {
    integrationId: string;
  }): Observable<Installation> {
    return this.listAll().pipe(
      map(
        (installations) =>
          installations.find(
            (installation) => installation.integrationId === integrationId
          )!
      )
    );
  }

  executeCommand<T = any>({
    installationId,
    action,
    body,
    connectionId,
    responseType
  }: {
    installationId: string;
    action: string;
    body: any;
    connectionId: string;
    responseType?: "json" | "text";
  }): Observable<T> {
    const url = `${environment.snoots.api.integrator}/installations/${installationId}/commands`;
    const payload = { action, connectionId, body };
    const context = new HttpContext().set(
      HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
      true
    );

    if (responseType === "text") {
      return this.http.post(url, payload, {
        context,
        responseType: "text"
      }) as Observable<T>;
    }

    return this.http.post<T>(url, payload, { context });
  }

  redeem({
    installationId,
    code,
    state
  }: {
    installationId: string;
    code: string;
    state: string;
  }): Observable<InstallationRedeemedCredentials> {
    return this.http.put<InstallationRedeemedCredentials>(
      `${environment.snoots.api.integrator}/oauth/redeem-code`,
      {
        installationId,
        code,
        state
      }
    );
  }

  @UserNotify({
    successMessage: marker(
      "Componente aggiuntivo abilitato, è necessario riavviare il PMS per completare l|operazione"
    ),
    errorMessage: marker("Impossibile abilitare il componente aggiuntivo"),
    actions: [
      {
        title: () => sntTranslate(marker("Riavvia")),
        variant: "primary",
        do: () => {
          window.location.reload();
        }
      }
    ]
  })
  enable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.integrator}/installations/${id}/enable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker(
      "Componente aggiuntivo disabilitato, è necessario riavviare il PMS per completare l|operazione"
    ),
    errorMessage: marker("Impossibile disattivare il componente aggiuntivo"),
    actions: [
      {
        title: () => sntTranslate(marker("Riavvia")),
        variant: "primary",
        do: () => {
          window.location.reload();
        }
      }
    ]
  })
  disable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.integrator}/installations/${id}/disable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Componente aggiuntivo disinstallato"),
    errorMessage: marker("Impossibile disinstallare il componente aggiuntivo")
  })
  uninstall(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.integrator}/installations/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Proprietà aggiornata"),
    errorMessage: marker("Impossibile aggiornare la proprietà")
  })
  updateProperty({
    id,
    propertyName,
    propertyValue
  }: {
    id: string;
    propertyName: string;
    propertyValue: string | number | boolean;
  }): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.integrator}/installations/${id}`,
      {
        configuration: {
          [propertyName]: propertyValue
        }
      }
    );
  }
}
